// Enhanced sub menu functionality for multiple menus
export function initSubMenu() {
  const DEBUG_SUB_MENU = false; // Set to true for debugging
  
  let isOpen = false;
  let currentOpenItem = null;
  let currentSubMenu = null;

  // Get elements - only mega menu items (solutions), not simple submenus
  const subMenuItems = document.querySelectorAll('.desktopNav__menu li.has-sub-menu.has-mega-menu');
  const subMenuContainers = document.querySelectorAll('.sub-menu-container');
  
  // Debug: Log found elements
  if (DEBUG_SUB_MENU) {
    console.log('Sub menu items found:', subMenuItems.length);
    console.log('Sub menu containers found:', subMenuContainers.length);
    subMenuItems.forEach((item, index) => {
      const classes = Array.from(item.classList);
      const menuItemClass = classes.find(cls => cls.startsWith('menu-item-'));
      console.log(`Item ${index}:`, menuItemClass);
    });
  }

  // Don't initialize on mobile
  if (window.matchMedia('(max-width: 1280px)').matches) {
    subMenuContainers.forEach(container => {
      container.style.display = 'none';
    });
    return;
  }

  // Show all sub menu containers on desktop
  subMenuContainers.forEach(container => {
    container.style.display = 'block';
  });

  // Get the correct sub menu container based on menu item
  function getSubMenuContainer(menuItem) {
    // Get all classes and find the menu-item-* class
    const classes = Array.from(menuItem.classList);
    const menuItemClass = classes.find(cls => cls.startsWith('menu-item-'));
    
    if (!menuItemClass) {
      if (DEBUG_SUB_MENU) {
        console.warn('No menu-item-* class found on menu item:', menuItem);
      }
      return null;
    }
    
    const menuType = menuItemClass.replace('menu-item-', ''); // Remove 'menu-item-' prefix
    
    // Dynamically find the sub menu container by looking for elements with matching IDs
    // The pattern is: {menuType}-sub-menu
    const subMenuId = `${menuType}-sub-menu`;
    const subMenuElement = document.getElementById(subMenuId);
    
    if (DEBUG_SUB_MENU) {
      console.log(`Looking for sub menu with ID: ${subMenuId}`);
      console.log('Found element:', subMenuElement);
    }
    
    if (subMenuElement) {
      const container = subMenuElement.closest('.sub-menu-container');
      if (DEBUG_SUB_MENU) {
        console.log('Found container:', container);
      }
      return container;
    }
    
    if (DEBUG_SUB_MENU) {
      console.warn(`No sub menu element found with ID: ${subMenuId}`);
    }
    
    return null;
  }

  // Simple show/hide functions
  function showSubMenu(item) {
    const subMenuContainer = getSubMenuContainer(item);
    
    if (DEBUG_SUB_MENU) {
      console.log('Attempting to show sub menu for item:', item);
      console.log('Found sub menu container:', subMenuContainer);
      console.log('Is already open:', isOpen);
    }
        
    if (subMenuContainer) {
      // Close any open regular dropdown menus
      const dropdownItems = document.querySelectorAll('.desktopNav__menu li.menu-item-has-children:not(.has-sub-menu)');
      dropdownItems.forEach(dropdownItem => {
        dropdownItem.classList.remove('submenu-open');
      });
      
      // Close any open simple submenus (Markets, Why Carleton)
      const simpleSubmenuItems = document.querySelectorAll('.desktopNav__menu li.has-sub-menu:not(.has-mega-menu)');
      simpleSubmenuItems.forEach(simpleItem => {
        simpleItem.classList.remove('submenu-open');
      });
      
      // Hide all other sub menus
      subMenuContainers.forEach(container => {
        container.classList.remove('active');
      });
      
      // Position simple submenus relative to their parent menu item BEFORE showing
      if (subMenuContainer.classList.contains('sub-menu-container--simple')) {
        const itemRect = item.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        
        // Align submenu's right edge with parent menu item's right edge
        const rightPosition = viewportWidth - itemRect.right;
        subMenuContainer.style.right = `${rightPosition}px`;
        subMenuContainer.style.left = 'auto';
      }
      
      // Show the correct sub menu
      subMenuContainer.classList.add('active');
      isOpen = true;
      currentOpenItem = item;
      currentSubMenu = subMenuContainer;
      item.classList.add('sub-menu-open');
    }
  }

  function hideSubMenu() {
    if (isOpen) {
      // Hide all sub menus
      subMenuContainers.forEach(container => {
        container.classList.remove('active');
      });
      
      isOpen = false;
      if (currentOpenItem) {
        currentOpenItem.classList.remove('sub-menu-open');
        currentOpenItem = null;
        currentSubMenu = null;
      }
    }
  }

  function toggleSubMenu(item) {
    if (isOpen && currentOpenItem === item) {
      hideSubMenu();
    } else {
      hideSubMenu(); // Close any currently open menu
      showSubMenu(item);
    }
  }

  // Add click events to sub menu items
  subMenuItems.forEach(item => {
    // Get the direct child link (first <a> that is a direct child of the <li>)
    const link = Array.from(item.children).find(child => child.tagName === 'A');
    if (link) {
      // Attach click handler to the list item, not just the link
      // This ensures clicks anywhere on the menu item (including padding/arrow area) work
      item.addEventListener('click', (e) => {
        // Only handle clicks on the parent link or the list item itself, not on child links
        const clickedLink = e.target.closest('a');
        const parentLink = link;
        
        // If clicking on a child link (in submenu), don't prevent default or toggle
        if (clickedLink && clickedLink !== parentLink) {
          return;
        }
        
        // If clicking on the parent link or anywhere else in the list item
        if (clickedLink === parentLink || !clickedLink) {
          e.preventDefault(); // Prevent default link behavior
          toggleSubMenu(item);
        }
      });
    }
  });

  // Close sub menu when clicking outside
  document.addEventListener('click', (e) => {
    if (isOpen && !currentSubMenu?.contains(e.target) && !Array.from(subMenuItems).some(item => item.contains(e.target))) {
      hideSubMenu();
    }
  });

  // Handle window resize
  window.addEventListener('resize', () => {
    if (window.innerWidth <= 1280) {
      // Mobile - hide all sub menus
      subMenuContainers.forEach(container => {
        container.style.display = 'none';
      });
      hideSubMenu();
    } else {
      // Desktop - show all sub menu containers
      subMenuContainers.forEach(container => {
        container.style.display = 'block';
      });
      
      // Reposition simple submenus if one is open
      if (isOpen && currentOpenItem && currentSubMenu?.classList.contains('sub-menu-container--simple')) {
        const itemRect = currentOpenItem.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const rightPosition = viewportWidth - itemRect.right;
        currentSubMenu.style.right = `${rightPosition}px`;
        currentSubMenu.style.left = 'auto';
      }
    }
  });

  // Close sub menus when scrolling
  let lastScrollY = window.scrollY;
  let scrollTimeout;
  
  window.addEventListener('scroll', () => {
    // Clear existing timeout
    clearTimeout(scrollTimeout);
    
    // Debounce scroll handler
    scrollTimeout = setTimeout(() => {
      const currentScrollY = window.scrollY;
      
      // Only close if actually scrolled (not just a scroll event)
      if (Math.abs(currentScrollY - lastScrollY) > 5) {
        hideSubMenu();
      }
      
      lastScrollY = currentScrollY;
    }, 50); // 50ms debounce
  }, { passive: true });
}
